import { addMonths, addYears, isAfter, isBefore, startOfMonth } from 'date-fns';
import { CARD_EXPIRATION_DATE_REGEX } from 'shared/utils/string/cardExpirationDate';
import {
  CARD_DIGITS_ONLY_REGEX,
  validateLuhn,
} from 'shared/utils/string/cardNumber';
import { z } from 'zod';

export const tabaPayValidationMessages = {
  invalidCardNumber: 'Invalid card number',
  expiryDateRequired: 'Required',
  invalidExpiryDate: 'Invalid date',
  cvvRequired: 'Required',
  invalidCvv: 'Security code must be 3 digits',
  cardRequired: 'Required',
  threeMonthsOut: 'Expires within 3 months',
};

const MAX_CARD_EXPIRY_YEARS = 20;

const parseExpirationDate = (value: string): Date => {
  const [monthText = '', yearText = ''] = value.split('/');
  const month = parseInt(monthText, 10);
  const year = parseInt(yearText, 10) + 2000;

  return startOfMonth(new Date(year, month - 1));
};

const tabaPayCardNumberSchema = z
  .string({ error: tabaPayValidationMessages.cardRequired })
  .min(1, { error: tabaPayValidationMessages.cardRequired })
  .transform((value) => value.replace(/\s/g, ''))
  .refine(
    (value) =>
      CARD_DIGITS_ONLY_REGEX.test(value) &&
      value.length === 16 &&
      validateLuhn(value),
    {
      error: tabaPayValidationMessages.invalidCardNumber,
    },
  );

const tabaPayExpirationDateSchema = z
  .string({ error: tabaPayValidationMessages.expiryDateRequired })
  .min(1, { error: tabaPayValidationMessages.expiryDateRequired })
  .regex(CARD_EXPIRATION_DATE_REGEX, {
    error: tabaPayValidationMessages.invalidExpiryDate,
  })
  .refine(
    (value) => isBefore(addMonths(new Date(), 3), parseExpirationDate(value)),
    {
      error: tabaPayValidationMessages.threeMonthsOut,
    },
  )
  .refine(
    (value) =>
      !isAfter(
        parseExpirationDate(value),
        addYears(startOfMonth(new Date()), MAX_CARD_EXPIRY_YEARS),
      ),
    {
      error: tabaPayValidationMessages.invalidExpiryDate,
    },
  );

const tabaPayCvvSchema = z
  .string({ error: tabaPayValidationMessages.cvvRequired })
  .min(1, { error: tabaPayValidationMessages.cvvRequired })
  .refine((value) => value.trim().length === 3, {
    error: tabaPayValidationMessages.invalidCvv,
  })
  .transform((value) => value.trim());

export const tabaPayCardFormSchema = z.object({
  cardNumber: tabaPayCardNumberSchema,
  date: tabaPayExpirationDateSchema,
  cvv: tabaPayCvvSchema,
  address: z
    .string({ error: tabaPayValidationMessages.cardRequired })
    .min(1, { error: tabaPayValidationMessages.cardRequired }),
});
